import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import ProgressBar from "@components/elements/ProgressBar";
import { PageConsultation } from "@components/layouts/PageConsultation";
import useCase from "@hooks/case/useCase";
import useComfortCare from "@hooks/recommendations/useComfortCare";
import { Button } from "@runwayhealth/runway-components-react";
import { StreamlinedSelection } from "@hooks/recommendations/useStreamlinedSelection";
import { addConditions, setConditions } from "@store/cases/casesSlice";
import { Case, RecommendedConditions } from "@types-runway/case";
import { Questions } from "@types-runway/questionnaire";
import { RootState } from "@types-runway/state";
import { Gender } from "@types-runway/user";

import RecommendationResults from "./RecommendationResults";
import TripDescriptionForm from "./TripDescriptionForm";

type StreamlinedConsultationPageProps = {
  title: string;
  reason: string;
  userGender: Gender;
  placeholder: string;
  regionRecommendations: RecommendedConditions;
  comfortCareRecommendations: RecommendedConditions | null;
  setComfortCareRecommendations: React.Dispatch<React.SetStateAction<RecommendedConditions | null>>;
  selection: StreamlinedSelection;
  onAnswer: (tripDescription: string) => void;
  isRegionLoading: boolean;
  isAuth: boolean;
};

const StreamlinedConsultationPage = ({
  title,
  reason,
  userGender,
  placeholder,
  regionRecommendations,
  comfortCareRecommendations,
  setComfortCareRecommendations,
  selection,
  onAnswer,
  isRegionLoading,
  isAuth,
}: StreamlinedConsultationPageProps) => {
  // --- State ---
  const dispatch = useDispatch();
  const { updateCase } = useCase();

  const newCase = useSelector<RootState, Case>((state) => state.case.newCase.data);
  const questions = useSelector<RootState, Questions>((state) => state.questions);
  // Page view state
  const [displayTripDescriptionForm, setDisplayTripDescriptionForm] = useState(
    regionRecommendations.conditions.length === 0
  );
  const [isFetchingComfortCare, setIsFetchingComfortCare] = useState(false);
  const [isSavingSelections, setIsSavingSelections] = useState(false);
  const [fetchingProgress, setFetchingProgress] = useState(0);

  // Form state
  const [tripDescription, setTripDescription] = useState<string>(
    Object.keys(questions).find((key) => key === "tripDescription")
      ? (questions["tripDescription"]?.answer as string)
      : ""
  );

  // Data state
  const { getComfortCare } = useComfortCare();

  // --- Derived State ---
  const showTripDescriptionForm = !isFetchingComfortCare && displayTripDescriptionForm;
  const showProgressBar = !showTripDescriptionForm && (isFetchingComfortCare || isRegionLoading);
  const showRecommendations = !displayTripDescriptionForm && !showProgressBar;
  const isNextButtonDisabled =
    showProgressBar ||
    isSavingSelections ||
    (displayTripDescriptionForm && tripDescription.trim() === "") ||
    tripDescription.length < 30;

  // --- Handlers & Effects ---

  const measureProgress = async <T,>(apiCall: () => Promise<T>): Promise<T> => {
    setIsFetchingComfortCare(true);
    setFetchingProgress(0);

    const progressInterval = setInterval(() => {
      setFetchingProgress((prev) => {
        if (prev >= 95) {
          clearInterval(progressInterval);
          return 95;
        }
        return prev + Math.random() * 15;
      });
    }, 500);

    try {
      const result = await apiCall();
      setFetchingProgress(100);
      return result;
    } finally {
      clearInterval(progressInterval);
      setTimeout(() => {
        setIsFetchingComfortCare(false);
      }, 500); // Wait for the 100% animation to finish
    }
  };

  // New recommendations start from a clean slate: the conditions the patient committed for the
  // earlier recommendations leave the case and the earlier choices are not carried over.
  const discardEarlierSelection = () => {
    const { committedConditionNames, clearSelection } = selection;
    if (committedConditionNames.length > 0) {
      const remainingConditions = newCase.conditions.filter(
        (condition) => !committedConditionNames.includes(condition.product.name)
      );
      // Redux first, so the case is clean even if the backend update fails.
      dispatch(setConditions({ conditions: remainingConditions }));
      updateCase(newCase.id ?? 0, isAuth, { conditions: remainingConditions });
    }
    clearSelection();
  };

  const fetchComfortCareRecommendations = async () => {
    discardEarlierSelection();
    setDisplayTripDescriptionForm(false);
    const comfortCare = await measureProgress(() => getComfortCare(tripDescription, userGender));
    setComfortCareRecommendations(comfortCare);
  };

  const submitSelectedConditions = () => {
    setIsSavingSelections(true);
    const selectedConditions = [...selection.regionConditions, ...selection.comfortCareConditions];
    const selectedNames = selectedConditions.map((condition) => condition.product.name);
    const withdrawnNames = selection.committedConditionNames.filter(
      (name) => !selectedNames.includes(name)
    );
    // Redux only: the questionnaire flushes the case's conditions to the backend on Next.
    if (withdrawnNames.length > 0) {
      dispatch(
        setConditions({
          conditions: newCase.conditions.filter(
            (condition) => !withdrawnNames.includes(condition.product.name)
          ),
        })
      );
    }
    dispatch(addConditions(selectedConditions));
    selection.setCommittedConditionNames(selectedNames);
  };

  const handleNext = useCallback(() => {
    onAnswer(tripDescription);
  }, [onAnswer, tripDescription]);

  const handleButtonClick = async () => {
    if (showRecommendations) {
      submitSelectedConditions();
    } else {
      await fetchComfortCareRecommendations();
    }
  };

  // Effect to handle navigating away after submitting conditions
  useEffect(() => {
    // When submission is triggered, this effect waits for the redux state to update
    // (via newCase.conditions dependency) and then navigates to the next page.
    if (isSavingSelections) {
      handleNext();
    }
  }, [newCase.conditions, isSavingSelections, handleNext]);

  // --- Render ---

  return (
    <>
      <PageConsultation.Body>
        {showTripDescriptionForm && (
          <TripDescriptionForm
            title={title}
            reason={reason}
            placeholder={placeholder}
            tripDescription={tripDescription}
            setTripDescription={setTripDescription}
          />
        )}
        {showProgressBar && (
          <>
            <h3 className="text-lg font-medium text-slate-800 mb-2">
              Getting your treatment plan ready
            </h3>
            <p className="text-slate-600 mb-4">
              We are now building your personalized treatment plan...
            </p>
            <ProgressBar value={fetchingProgress} />
          </>
        )}
        {showRecommendations && (
          <RecommendationResults
            regionRecommendations={regionRecommendations}
            comfortCareRecommendations={
              comfortCareRecommendations ?? { conditions: [], reason: "" }
            }
            selection={selection}
          />
        )}
      </PageConsultation.Body>
      <PageConsultation.Footer>
        <Button
          className="next-button"
          size="lg"
          iconName="ArrowRight"
          customWidth={440}
          onClick={handleButtonClick}
          disabled={isNextButtonDisabled}
          isLoading={showProgressBar || isSavingSelections}
        >
          Next
        </Button>
      </PageConsultation.Footer>
    </>
  );
};

export default StreamlinedConsultationPage;
